import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  AreaChart, Area
} from 'recharts';
import { 
  DollarSign, Users, Calendar, TrendingUp, Briefcase, 
  Package, Clock, CheckCircle2, XCircle, AlertCircle,
  ArrowUpRight, ArrowDownRight, Loader2, RefreshCw,
  Star, MapPin
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COLORS = ['#6366f1', '#8b5cf6', '#a855f7', '#d946ef', '#ec4899', '#f43f5e', '#f97316', '#eab308'];

export default function AnalyticsDashboard() {
  const navigate = useNavigate();
  const { token, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [dateRange, setDateRange] = useState('30');
  
  // Data states
  const [overview, setOverview] = useState(null);
  const [revenueTrend, setRevenueTrend] = useState([]);
  const [bookingsByStatus, setBookingsByStatus] = useState({});
  const [popularServices, setPopularServices] = useState([]);
  const [userGrowth, setUserGrowth] = useState([]);
  const [vendorPerformance, setVendorPerformance] = useState([]);
  const [upcomingEvents, setUpcomingEvents] = useState(null);
  const [categoryBreakdown, setCategoryBreakdown] = useState([]);

  useEffect(() => {
    if (!token) {
      navigate('/login');
      return;
    }
    if (user?.role !== 'admin') {
      navigate('/');
      return;
    }
    fetchAllData();
  }, [token, user, navigate]);

  useEffect(() => {
    if (token && user?.role === 'admin') {
      fetchTrendData();
    }
  }, [dateRange]);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        fetchOverview(),
        fetchTrendData(),
        fetchBookingsByStatus(),
        fetchPopularServices(),
        fetchVendorPerformance(),
        fetchUpcomingEvents(),
        fetchCategoryBreakdown()
      ]);
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchOverview = async () => {
    try {
      const res = await axios.get(`${API}/admin/analytics/overview`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOverview(res.data);
    } catch (error) {
      console.error('Failed to fetch overview:', error);
    }
  };

  const fetchTrendData = async () => {
    try {
      const [revenueRes, growthRes] = await Promise.all([
        axios.get(`${API}/admin/analytics/revenue-trend?days=${dateRange}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/admin/analytics/user-growth?days=${dateRange}`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      setRevenueTrend(revenueRes.data.trend || []);
      setUserGrowth(growthRes.data.growth || []);
    } catch (error) {
      console.error('Failed to fetch trends:', error);
    }
  };

  const fetchBookingsByStatus = async () => {
    try {
      const res = await axios.get(`${API}/admin/analytics/bookings-by-status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBookingsByStatus(res.data.by_status || {});
    } catch (error) {
      console.error('Failed to fetch bookings by status:', error);
    }
  };

  const fetchPopularServices = async () => {
    try {
      const res = await axios.get(`${API}/admin/analytics/popular-services?limit=8`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPopularServices(res.data.services || []);
    } catch (error) {
      console.error('Failed to fetch popular services:', error);
    }
  };

  const fetchVendorPerformance = async () => {
    try {
      const res = await axios.get(`${API}/admin/analytics/vendor-performance?limit=5`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setVendorPerformance(res.data.vendors || []);
    } catch (error) {
      console.error('Failed to fetch vendor performance:', error);
    }
  };

  const fetchUpcomingEvents = async () => {
    try {
      const res = await axios.get(`${API}/admin/analytics/upcoming-events?days=30`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUpcomingEvents(res.data);
    } catch (error) {
      console.error('Failed to fetch upcoming events:', error);
    }
  };

  const fetchCategoryBreakdown = async () => {
    try {
      const res = await axios.get(`${API}/admin/analytics/category-breakdown`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCategoryBreakdown(res.data.categories || []);
    } catch (error) {
      console.error('Failed to fetch category breakdown:', error);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchAllData();
    setRefreshing(false);
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  const formatDate = (dateStr) => {
    try {
      return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    } catch {
      return dateStr;
    }
  };

  // Prepare pie chart data for bookings by status
  const statusPieData = Object.entries(bookingsByStatus).map(([status, count]) => ({
    name: status.charAt(0).toUpperCase() + status.slice(1),
    value: count
  })).filter(d => d.value > 0);

  if (loading) {
    return (
      <div className="min-h-screen bg-background pt-20 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-indigo-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pt-20 pb-16" data-testid="analytics-dashboard">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold flex items-center gap-3" data-testid="analytics-title">
              <TrendingUp className="h-6 w-6 sm:h-8 sm:w-8 text-indigo-500" />
              Analytics Dashboard
            </h1>
            <p className="text-muted-foreground mt-1 text-sm sm:text-base">
              Monitor your business performance and insights
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Select value={dateRange} onValueChange={setDateRange}>
              <SelectTrigger className="w-32">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="7">Last 7 days</SelectItem>
                <SelectItem value="14">Last 14 days</SelectItem>
                <SelectItem value="30">Last 30 days</SelectItem>
                <SelectItem value="90">Last 90 days</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={handleRefresh} disabled={refreshing}>
              <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <Card className="bg-gradient-to-br from-indigo-500/10 to-indigo-500/5 border-indigo-500/20">
            <CardContent className="p-4 sm:p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs sm:text-sm text-muted-foreground">Total Revenue</p>
                  <p className="text-xl sm:text-2xl font-bold text-indigo-500">
                    {formatCurrency(overview?.revenue?.total || 0)}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {formatCurrency(overview?.revenue?.pending || 0)} pending
                  </p>
                </div>
                <div className="p-2 sm:p-3 rounded-xl bg-indigo-500/20">
                  <DollarSign className="h-5 w-5 sm:h-6 sm:w-6 text-indigo-500" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-green-500/10 to-green-500/5 border-green-500/20">
            <CardContent className="p-4 sm:p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs sm:text-sm text-muted-foreground">Net Revenue</p>
                  <p className="text-xl sm:text-2xl font-bold text-green-500">
                    {formatCurrency(overview?.revenue?.net_revenue || 0)}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    After vendor payouts
                  </p>
                </div>
                <div className="p-2 sm:p-3 rounded-xl bg-green-500/20">
                  <TrendingUp className="h-5 w-5 sm:h-6 sm:w-6 text-green-500" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-purple-500/10 to-purple-500/5 border-purple-500/20">
            <CardContent className="p-4 sm:p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs sm:text-sm text-muted-foreground">Total Users</p>
                  <p className="text-xl sm:text-2xl font-bold text-purple-500">
                    {overview?.users?.total || 0}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {overview?.users?.vendors || 0} vendors
                  </p>
                </div>
                <div className="p-2 sm:p-3 rounded-xl bg-purple-500/20">
                  <Users className="h-5 w-5 sm:h-6 sm:w-6 text-purple-500" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-amber-500/10 to-amber-500/5 border-amber-500/20">
            <CardContent className="p-4 sm:p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs sm:text-sm text-muted-foreground">Paid Bookings</p>
                  <p className="text-xl sm:text-2xl font-bold text-amber-500">
                    {overview?.bookings?.total_paid || 0}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {overview?.bookings?.active_workspaces || 0} active
                  </p>
                </div>
                <div className="p-2 sm:p-3 rounded-xl bg-amber-500/20">
                  <Calendar className="h-5 w-5 sm:h-6 sm:w-6 text-amber-500" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Charts Section */}
        <div className="grid lg:grid-cols-2 gap-6 mb-8">
          {/* Revenue Trend */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Revenue Trend</CardTitle>
              <CardDescription>Daily revenue over the selected period</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-[300px]">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={revenueTrend}>
                    <defs>
                      <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#6366f1" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="#6366f1" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                    <XAxis 
                      dataKey="date" 
                      tickFormatter={formatDate}
                      className="text-xs"
                      tick={{ fill: 'currentColor' }}
                    />
                    <YAxis 
                      tickFormatter={(v) => `$${v}`}
                      className="text-xs"
                      tick={{ fill: 'currentColor' }}
                    />
                    <Tooltip 
                      formatter={(value) => [formatCurrency(value), 'Revenue']}
                      labelFormatter={formatDate}
                      contentStyle={{ backgroundColor: 'hsl(var(--card))', border: '1px solid hsl(var(--border))' }}
                    />
                    <Area 
                      type="monotone" 
                      dataKey="revenue" 
                      stroke="#6366f1" 
                      fillOpacity={1} 
                      fill="url(#colorRevenue)" 
                      strokeWidth={2}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* User Growth */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">User Growth</CardTitle>
              <CardDescription>New signups over the selected period</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-[300px]">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={userGrowth}>
                    <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                    <XAxis 
                      dataKey="date" 
                      tickFormatter={formatDate}
                      className="text-xs"
                      tick={{ fill: 'currentColor' }}
                    />
                    <YAxis 
                      className="text-xs"
                      tick={{ fill: 'currentColor' }}
                    />
                    <Tooltip 
                      labelFormatter={formatDate}
                      contentStyle={{ backgroundColor: 'hsl(var(--card))', border: '1px solid hsl(var(--border))' }}
                    />
                    <Legend />
                    <Line type="monotone" dataKey="users" stroke="#8b5cf6" strokeWidth={2} dot={false} name="Users" />
                    <Line type="monotone" dataKey="vendors" stroke="#f97316" strokeWidth={2} dot={false} name="Vendors" />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Second Row */}
        <div className="grid lg:grid-cols-3 gap-6 mb-8">
          {/* Bookings by Status */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Bookings by Status</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-[250px]">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={statusPieData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={80}
                      fill="#8884d8"
                      paddingAngle={5}
                      dataKey="value"
                      label={({ name, value }) => `${name}: ${value}`}
                    >
                      {statusPieData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="flex flex-wrap justify-center gap-2 mt-4">
                {statusPieData.map((entry, index) => (
                  <div key={entry.name} className="flex items-center gap-1 text-xs">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS[index % COLORS.length] }} />
                    <span>{entry.name}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Category Breakdown */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Revenue by Category</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-[250px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={categoryBreakdown.slice(0, 6)} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                    <XAxis type="number" tickFormatter={(v) => `$${v}`} tick={{ fill: 'currentColor' }} />
                    <YAxis dataKey="category" type="category" width={80} tick={{ fill: 'currentColor', fontSize: 12 }} />
                    <Tooltip formatter={(value) => [formatCurrency(value), 'Revenue']} />
                    <Bar dataKey="revenue" fill="#6366f1" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* Popular Services */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Popular Services</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {popularServices.slice(0, 5).map((service, idx) => (
                  <div key={idx} className="flex items-center justify-between p-2 rounded-lg hover:bg-muted/50">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg bg-indigo-500/20 flex items-center justify-center text-indigo-500 font-bold text-sm">
                        {idx + 1}
                      </div>
                      <div>
                        <p className="font-medium text-sm truncate max-w-[120px]">{service.name}</p>
                        <p className="text-xs text-muted-foreground">{service.bookings} bookings</p>
                      </div>
                    </div>
                    <span className="font-semibold text-green-500 text-sm">{formatCurrency(service.revenue)}</span>
                  </div>
                ))}
                {popularServices.length === 0 && (
                  <p className="text-center text-muted-foreground text-sm py-4">No data yet</p>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Third Row */}
        <div className="grid lg:grid-cols-2 gap-6">
          {/* Top Vendors */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Top Performing Vendors</CardTitle>
              <CardDescription>By total earnings</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {vendorPerformance.map((vendor, idx) => (
                  <div key={vendor.vendor_id} className="flex items-center justify-between p-3 border rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-foreground ${
                        idx === 0 ? 'bg-amber-500' : idx === 1 ? 'bg-slate-400' : idx === 2 ? 'bg-amber-700' : 'bg-slate-500'
                      }`}>
                        {idx + 1}
                      </div>
                      <div>
                        <p className="font-medium">{vendor.name}</p>
                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                          <span>{vendor.jobs} jobs</span>
                          {vendor.rating > 0 && (
                            <span className="flex items-center gap-1">
                              <Star className="h-3 w-3 fill-yellow-500 text-yellow-500" />
                              {vendor.rating?.toFixed(1)}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <span className="font-bold text-green-500">{formatCurrency(vendor.earnings)}</span>
                  </div>
                ))}
                {vendorPerformance.length === 0 && (
                  <p className="text-center text-muted-foreground text-sm py-8">No vendor data yet</p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Upcoming Events */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Upcoming Events</CardTitle>
              <CardDescription>{upcomingEvents?.upcoming_count || 0} events in the next 30 days</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {upcomingEvents?.events?.slice(0, 5).map((event) => (
                  <div 
                    key={event.workspace_id} 
                    className="flex items-center justify-between p-3 border rounded-lg cursor-pointer hover:border-indigo-300 transition-colors"
                    onClick={() => navigate(`/admin/workspaces/${event.workspace_id}`)}
                  >
                    <div>
                      <p className="font-medium">{event.title}</p>
                      <div className="flex items-center gap-3 text-xs text-muted-foreground mt-1">
                        <span className="flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          {new Date(event.event_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                        </span>
                        <span className="flex items-center gap-1">
                          <MapPin className="h-3 w-3" />
                          {event.event_location || 'TBD'}
                        </span>
                        <span className="flex items-center gap-1">
                          <Users className="h-3 w-3" />
                          {event.guest_count}
                        </span>
                      </div>
                    </div>
                    <Badge variant="outline" className="text-green-500 border-green-500">
                      {event.status}
                    </Badge>
                  </div>
                ))}
                {(!upcomingEvents?.events || upcomingEvents.events.length === 0) && (
                  <p className="text-center text-muted-foreground text-sm py-8">No upcoming events</p>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
